import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { EventData, EventNames, useTracking } from "lib/tracking";
import { LeadEntityType } from "lib/types/graphQLEnums";
import { graphql } from "../../../generated/app-service-gql";
import {
  RemoveLeadEntitiesMutation,
  RemoveLeadEntitiesMutationVariables,
} from "../../../generated/app-service-gql/graphql";
import { useGraphQlMutation } from "../useGraphQlClient";
import { updateLeadEntityCache } from "./update_cache/updateLeadEntityCache";
import { leadEntityEventData } from "./leadEntityTracking";

const REMOVE_LEAD_ENTITIES_MUTATION = graphql(`
  mutation removeLeadEntities($id: String!, $entities: LeadEntitiesInput!) {
    removeLeadEntities(id: $id, entities: $entities) {
      leadId
      leadName
      entityId
      entityType
    }
  }
`);

/**
 * @param trackingData - Extra attributes for the "Lead data removed" event. A function is called
 * with the succeeding remove's variables, for attributes that belong to that one call.
 */
export function useRemoveLeadEntities(
  trackingData?:
    | EventData
    | ((variables: RemoveLeadEntitiesMutationVariables) => EventData | undefined),
  options?: UseMutationOptions<
    RemoveLeadEntitiesMutation,
    unknown,
    RemoveLeadEntitiesMutationVariables,
    unknown
  >,
) {
  const { logEvent } = useTracking();
  const queryClient = useQueryClient();

  return useGraphQlMutation(REMOVE_LEAD_ENTITIES_MUTATION, {
    ...options,
    onSuccess: async (data, variables, context) => {
      const entities = data.removeLeadEntities;

      // Update lead entities cache for removal
      await updateLeadEntityCache({
        entities,
        isAdding: false,
        queryClient,
      });

      const hasCustomBuyer = variables.entities.entities.some(
        (e) => e.entityType === LeadEntityType.CustomOrganisation,
      );

      const callTrackingData =
        typeof trackingData === "function" ? trackingData(variables) : trackingData;
      logEvent(EventNames.leadDataRemoved, {
        ...callTrackingData,
        ...leadEntityEventData(
          variables.entities.entities.map((e) => e.entityType),
          callTrackingData,
        ),
        "Lead ID": variables.id,
        "Amount of entities": entities.length,
        "Free-text buyer": hasCustomBuyer,
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
}
